import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaSave,
  FaCheck,
  FaTimes,
  FaCar,
  FaWarehouse,
  FaCity,
  FaMapPin,
  FaImage,
  FaSearchLocation,
  FaUser,
  FaParking,
  FaLocationArrow,
  FaPlusCircle,
  FaTrash,
  FaInfoCircle,
  FaExclamationTriangle,
  FaGlobeAmericas,
  FaLayerGroup,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import axios from "axios";
import { Baseurl } from "../../baseurl";
import { useNavigate, useParams } from "react-router-dom";

const EditYard = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get yard ID from URL params

  // Form state
  const [formData, setFormData] = useState({
    owner: "",
    yardName: "",
    yardStatus: "operational",
    yardImage: "",
    occupancy: "",
    yardAddress: "",
    city: "",
    state: "",
    coordinates: { lat: 0, lng: 0 },
    parkedVehicles: 0,
    parkingSpots: [],
    isVerified: false,
    isActive: true,
  });

  // Parking spot form
  const [parkingSpotForm, setParkingSpotForm] = useState({
    spotNumber: "",
    spotName: "",
    status: "available",
    size: "medium",
  });

  // UI state
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(true);
  const [fetchingYard, setFetchingYard] = useState(true);
  const [errors, setErrors] = useState({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState("");
  const [geocodeSuccess, setGeocodeSuccess] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitError, setSubmitError] = useState("");

  // Fetch employees and yard data on component mount
  useEffect(() => {
    fetchEmployees();
    fetchYardDetails();
  }, [id]);

  const fetchEmployees = async () => {
    try {
      setFetchingEmployees(true);
      const response = await axios.get(`${Baseurl}/api/employees/all`);
      setEmployees(response.data?.emp || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setSubmitError("Failed to load employees. Please refresh the page.");
    } finally {
      setFetchingEmployees(false);
    }
  };

  const fetchYardDetails = async () => {
    if (!id) {
      setSubmitError("No yard ID provided");
      setFetchingYard(false);
      return;
    }

    try {
      setFetchingYard(true);
      const response = await axios.post(
        `${Baseurl}/api/yards/getIndividualYardDetails`,
        { _id: id },
      );
      console.log(response.data, "response.data");
      if (response.data.success) {
        const yardData = response.data.yard || response.data.data;

        // Map API response to form state
        setFormData({
          owner: yardData.owner || "",
          yardName: yardData.yardName || "",
          yardStatus: yardData.yardStatus || "operational",
          yardImage: yardData.yardImage || "",
          occupancy: yardData.occupancy || "",
          yardAddress: yardData.yardAddress || "",
          city: yardData.city || "",
          state: yardData.state || "",
          coordinates: {
            lat: yardData.coordinates?.lat || yardData.latitude || 0,
            lng: yardData.coordinates?.lng || yardData.longitude || 0,
          },
          parkedVehicles: yardData.parkedVehicles || 0,
          parkingSpots:
            yardData.parkingSpots?.map((spot) => ({
              ...spot,
              _id: spot._id || Date.now().toString(),
            })) || [],
          isVerified: yardData.isVerified || false,
          isActive: yardData.isActive !== undefined ? yardData.isActive : true,
        });

        // If coordinates exist, set geocode success
        if (
          (yardData.coordinates?.lat && yardData.coordinates?.lat !== 0) ||
          (yardData.coordinates?.lng && yardData.coordinates?.lng !== 0)
        ) {
          setGeocodeSuccess(true);
        }
      } else {
        setSubmitError("Failed to fetch yard details");
      }
    } catch (error) {
      console.error("Error fetching yard details:", error);
      setSubmitError(
        error.response?.data?.message ||
          "Failed to load yard details. Please try again.",
      );
    } finally {
      setFetchingYard(false);
    }
  };

  // Handle main form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear errors for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle parking spot form changes
  const handleParkingSpotChange = (e) => {
    const { name, value } = e.target;
    setParkingSpotForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle coordinate changes
  const handleCoordinateChange = (e) => {
    const { name, value } = e.target;
    const [parent, child] = name.split(".");

    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: parseFloat(value) || 0,
      },
    }));
  };

  // Add a parking spot
  const addParkingSpot = () => {
    if (!parkingSpotForm.spotNumber.trim()) {
      setErrors((prev) => ({
        ...prev,
        parkingSpot: "Spot number is required",
      }));
      return;
    }

    // Check for duplicate spot numbers
    const spotExists = formData.parkingSpots.some(
      (spot) => spot.spotNumber === parkingSpotForm.spotNumber.trim(),
    );

    if (spotExists) {
      setErrors((prev) => ({
        ...prev,
        parkingSpot: "Spot number already exists",
      }));
      return;
    }

    const newSpot = {
      ...parkingSpotForm,
      _id: Date.now().toString(),
      spotNumber: parkingSpotForm.spotNumber.trim(),
      spotName:
        parkingSpotForm.spotName.trim() || `Spot ${parkingSpotForm.spotNumber}`,
      vehicle: null,
      occupiedAt: null,
      expectedDeparture: null,
    };

    setFormData((prev) => ({
      ...prev,
      parkingSpots: [...prev.parkingSpots, newSpot],
    }));

    // Reset parking spot form
    setParkingSpotForm({
      spotNumber: "",
      spotName: "",
      status: "available",
      size: "medium",
    });

    // Clear parking spot errors
    if (errors.parkingSpot) {
      setErrors((prev) => ({ ...prev, parkingSpot: "" }));
    }
  };

  // Remove a parking spot
  const removeParkingSpot = (index) => {
    setFormData((prev) => ({
      ...prev,
      parkingSpots: prev.parkingSpots.filter((_, i) => i !== index),
    }));
  };

  // Geocode address
  const geocodeAddress = async () => {
    if (!formData.yardAddress?.trim()) {
      setGeocodeError("Please enter address first");
      return;
    }
    if (!formData.city?.trim()) {
      setGeocodeError("Please enter city first");
      return;
    }
    if (!formData.state?.trim()) {
      setGeocodeError("Please enter state first");
      return;
    }

    const fullAddress = `${formData.yardAddress}, ${formData.city}, ${formData.state}`;

    setGeocodingLoading(true);
    setGeocodeError("");
    setGeocodeSuccess(false);

    try {
      // Add delay to respect Nominatim's usage policy
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
        {
          headers: {
            "User-Agent": "YardManagementSystem/1.0",
          },
        },
      );

      if (!response.ok) throw new Error("Geocoding service unavailable");

      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);

        setFormData((prev) => ({
          ...prev,
          coordinates: { lat, lng },
        }));

        setGeocodeSuccess(true);
        setGeocodeError("");

        if (errors.coordinates) {
          setErrors((prev) => ({ ...prev, coordinates: "" }));
        }
      } else {
        setGeocodeError(
          "Address not found. Please check the address and try again.",
        );
        setGeocodeSuccess(false);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setGeocodeError(
        "Failed to fetch coordinates. Please try again in a moment.",
      );
      setGeocodeSuccess(false);
    } finally {
      setGeocodingLoading(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.yardName?.trim())
      newErrors.yardName = "Yard name is required";
    if (!formData.yardAddress?.trim())
      newErrors.yardAddress = "Address is required";
    if (!formData.city?.trim()) newErrors.city = "City is required";
    if (!formData.state?.trim()) newErrors.state = "State is required";
    if (!formData.state?.trim() || formData.state.length < 2)
      newErrors.state = "Enter valid state code";
    if (formData.coordinates.lat === 0 && formData.coordinates.lng === 0) {
      newErrors.coordinates = "Please fetch coordinates from address";
    }
    if (
      formData.occupancy &&
      (isNaN(formData.occupancy) || parseInt(formData.occupancy) < 0)
    ) {
      newErrors.occupancy = "Must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitSuccess("");
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        _id: id, // Include the yard ID for update
        occupancy: formData.parkingSpots.length,
        state: formData.state.toUpperCase(),
        parkingSpots: formData.parkingSpots.map(({ _id, ...spot }) => spot),
      };

      const response = await axios.put(
        `${Baseurl}/api/yards/updateYardDetails`,
        submitData,
      );

      if (response.data.success) {
        setSubmitSuccess("✅ Yard updated successfully!");

        // Redirect after delay
        setTimeout(() => {
          navigate("/yards/list");
        }, 1500);
      }
    } catch (error) {
      console.error("Error updating yard:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update yard. Please check your connection and try again.";
      setSubmitError(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Check if fetch coordinates button should be enabled
  const isFetchEnabled =
    formData.yardAddress?.trim() &&
    formData.city?.trim() &&
    formData.state?.trim();

  // Check if form is ready for submission
  const isFormReady =
    formData.yardName?.trim() &&
    formData.yardAddress?.trim() &&
    formData.city?.trim() &&
    formData.state?.trim() &&
    formData.state.length >= 2 &&
    formData.coordinates.lat !== 0;

  // Loading state
  if (fetchingYard) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading yard details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4">
      {/* Main Form Card */}
      <Card className="shadow-lg border-0 rounded-3 overflow-hidden">
        {/* Card Header with Gradient */}
        <Card.Header
          className="py-3"
          style={{
            background: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
          }}
        >
          <div className="d-flex align-items-center text-white">
            <div className="bg-white bg-opacity-20 p-2 rounded-circle me-3">
              <FaWarehouse size={24} />
            </div>
            <div>
              <h4 className="mb-0 fw-bold">Edit Yard Information</h4>
              <small className="opacity-75">
                Update yard details and parking spots
              </small>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          {/* Status Messages */}
          {submitSuccess && (
            <Alert
              variant="success"
              className="border-0 shadow-sm d-flex align-items-center"
            >
              <FaCheck className="me-3 fs-4" />
              <div className="flex-grow-1">
                <strong>Success!</strong>
                <div className="small">{submitSuccess}</div>
              </div>
            </Alert>
          )}

          {submitError && (
            <Alert
              variant="danger"
              className="border-0 shadow-sm d-flex align-items-center"
            >
              <FaExclamationTriangle className="me-3 fs-4" />
              <div className="flex-grow-1">
                <strong>Error!</strong>
                <div className="small">{submitError}</div>
              </div>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Section 1: Basic Information */}
            <div className="mb-5">
              <div className="d-flex align-items-center mb-4">
                <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-3">
                  <FaWarehouse className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Basic Information</h5>
                  <small className="text-muted">
                    Update the basic details of the yard
                  </small>
                </div>
              </div>

              <Row className="g-3">
                {/* Yard Name */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Yard Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="yardName"
                      value={formData.yardName}
                      onChange={handleChange}
                      isInvalid={!!errors.yardName}
                      placeholder="Enter yard name"
                      className="border-2 py-2"
                    />
                    <Form.Control.Feedback type="invalid">
                      <FaExclamationTriangle className="me-2" />
                      {errors.yardName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Owner Selection */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Owner</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-end-0">
                        <FaUser className="text-primary" />
                      </InputGroup.Text>
                      <Form.Select
                        name="owner"
                        value={formData.owner}
                        onChange={handleChange}
                        className="border-start-0 py-2"
                        disabled={fetchingEmployees}
                      >
                        <option value="">Select an owner...</option>
                        {fetchingEmployees ? (
                          <option>Loading employees...</option>
                        ) : employees.length > 0 ? (
                          employees.map((emp) => (
                            <option key={emp._id} value={emp._id}>
                              {emp.name || emp.email}
                            </option>
                          ))
                        ) : (
                          <option>No employees available</option>
                        )}
                      </Form.Select>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      {fetchingEmployees
                        ? "Fetching employee list..."
                        : `${employees.length} employees available`}
                    </Form.Text>
                  </Form.Group>
                </Col>

                {/* Yard Status */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Yard Status</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light">
                        <span
                          className={`me-2 ${formData.yardStatus === "operational" ? "text-success" : "text-warning"}`}
                        >
                          ●
                        </span>
                      </InputGroup.Text>
                      <Form.Select
                        name="yardStatus"
                        value={formData.yardStatus}
                        onChange={handleChange}
                        className="py-2"
                      >
                        <option value="operational">🟢 Operational</option>
                        <option value="maintenance">🟡 Maintenance</option>
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>
                </Col>

                {/* Yard Image */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Yard Image URL (Optional)
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light">
                        <FaImage className="text-primary" />
                      </InputGroup.Text>
                      <Form.Control
                        type="url"
                        name="yardImage"
                        value={formData.yardImage}
                        onChange={handleChange}
                        placeholder="https://example.com/yard-image.jpg"
                        className="border-2 py-2"
                      />
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Provide a direct link to an image of the yard
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Section 2: Location Details */}
            <div className="mb-5">
              <div className="d-flex align-items-center mb-4">
                <div className="bg-info bg-opacity-10 p-2 rounded-circle me-3">
                  <FaMapMarkerAlt className="text-info" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Location Details</h5>
                  <small className="text-muted">
                    Update address and coordinates
                  </small>
                </div>
              </div>

              <Row className="g-3">
                {/* Address */}
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      Street Address <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light">
                        <FaMapMarkerAlt className="text-info" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="yardAddress"
                        value={formData.yardAddress}
                        onChange={handleChange}
                        isInvalid={!!errors.yardAddress}
                        placeholder="123 Main Street, Apt 4B"
                        className="border-2 py-2"
                      />
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.yardAddress}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* City */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light">
                        <FaCity className="text-info" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        isInvalid={!!errors.city}
                        placeholder="New York"
                        className="border-2 py-2"
                      />
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* State */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      State Code <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light">
                        <FaMapPin className="text-info" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        isInvalid={!!errors.state}
                        placeholder="NY"
                        className="border-2 py-2 text-uppercase"
                        style={{ textTransform: "uppercase" }}
                      />
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.state}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Enter 2-letter state code (e.g., CA, NY, TX)
                    </Form.Text>
                  </Form.Group>
                </Col>

                {/* Fetch Coordinates Button */}
                <Col xs={12}>
                  <div className="mb-3">
                    <Button
                      variant={geocodeSuccess ? "success" : "primary"}
                      onClick={geocodeAddress}
                      disabled={!isFetchEnabled || geocodingLoading}
                      className="w-100 py-3 rounded-pill shadow-sm"
                      style={{
                        background: geocodeSuccess
                          ? ""
                          : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        border: "none",
                      }}
                    >
                      {geocodingLoading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Fetching Coordinates...
                        </>
                      ) : (
                        <>
                          <FaSearchLocation className="me-2" />
                          {geocodeSuccess
                            ? "✓ Coordinates Fetched Successfully!"
                            : "📍 Update Coordinates from Address"}
                        </>
                      )}
                    </Button>

                    {geocodeError && (
                      <Alert
                        variant="danger"
                        className="mt-3 border-0 shadow-sm d-flex align-items-center"
                      >
                        <FaExclamationTriangle className="me-2 flex-shrink-0" />
                        <span className="flex-grow-1">{geocodeError}</span>
                      </Alert>
                    )}

                    {geocodeSuccess && !geocodeError && (
                      <Alert
                        variant="success"
                        className="mt-3 border-0 shadow-sm d-flex align-items-center"
                      >
                        <FaCheck className="me-2 flex-shrink-0" />
                        <span className="flex-grow-1">
                          Coordinates fetched successfully! Latitude and
                          longitude fields are now populated below.
                        </span>
                      </Alert>
                    )}
                  </div>
                </Col>

                {/* Coordinates Display */}
                {(formData.coordinates.lat !== 0 ||
                  formData.coordinates.lng !== 0) && (
                  <>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Latitude</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-light">
                            <FaGlobeAmericas className="text-info" />
                          </InputGroup.Text>
                          <Form.Control
                            type="number"
                            step="any"
                            name="coordinates.lat"
                            value={formData.coordinates.lat}
                            onChange={handleCoordinateChange}
                            placeholder="Latitude"
                            className="border-2 py-2"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Longitude</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-light">
                            <FaGlobeAmericas className="text-info" />
                          </InputGroup.Text>
                          <Form.Control
                            type="number"
                            step="any"
                            name="coordinates.lng"
                            value={formData.coordinates.lng}
                            onChange={handleCoordinateChange}
                            placeholder="Longitude"
                            className="border-2 py-2"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    {errors.coordinates && (
                      <Col xs={12}>
                        <Alert variant="warning" className="border-0 shadow-sm">
                          <FaExclamationTriangle className="me-2" />
                          {errors.coordinates}
                        </Alert>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </div>

            {/* Section 3: Parking Spots */}
            <div className="mb-5">
              <div className="d-flex align-items-center mb-4">
                <div className="bg-warning bg-opacity-10 p-2 rounded-circle me-3">
                  <FaParking className="text-warning" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Parking Spots</h5>
                  <small className="text-muted">
                    Manage parking spots ({formData.parkingSpots.length} total)
                  </small>
                </div>
              </div>

              {/* Add Parking Spot Card */}
              <Card className="border-warning mb-4 shadow-sm">
                <Card.Header className="bg-warning bg-opacity-10 border-warning">
                  <h6 className="mb-0">
                    <FaPlusCircle className="me-2 text-warning" />
                    Add New Parking Spot
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-2">
                    <Col md={3}>
                      <Form.Control
                        type="text"
                        name="spotNumber"
                        value={parkingSpotForm.spotNumber}
                        onChange={handleParkingSpotChange}
                        placeholder="Spot Number *"
                        className="mb-2 py-2"
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        type="text"
                        name="spotName"
                        value={parkingSpotForm.spotName}
                        onChange={handleParkingSpotChange}
                        placeholder="Spot Name (Optional)"
                        className="mb-2 py-2"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Select
                        name="status"
                        value={parkingSpotForm.status}
                        onChange={handleParkingSpotChange}
                        className="mb-2 py-2"
                      >
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="reserved">Reserved</option>
                        <option value="maintenance">Maintenance</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select
                        name="size"
                        value={parkingSpotForm.size}
                        onChange={handleParkingSpotChange}
                        className="mb-2 py-2"
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Button
                        variant="warning"
                        onClick={addParkingSpot}
                        className="w-100 mb-2 py-2"
                      >
                        <FaPlusCircle className="me-1" />
                        Add Spot
                      </Button>
                    </Col>
                  </Row>
                  {errors.parkingSpot && (
                    <Alert variant="danger" className="mt-2 py-2">
                      <FaExclamationTriangle className="me-2" />
                      {errors.parkingSpot}
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Parking Spots List */}
              {formData.parkingSpots.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Parking Spots</h6>
                    <Badge bg="primary" pill>
                      {formData.parkingSpots.length} spots
                    </Badge>
                  </div>
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th width="15%">Spot #</th>
                          <th width="25%">Name</th>
                          <th width="20%">Status</th>
                          <th width="15%">Size</th>
                          <th width="15%">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.parkingSpots.map((spot, index) => (
                          <tr key={spot._id || index}>
                            <td className="fw-bold">{spot.spotNumber}</td>
                            <td>{spot.spotName}</td>
                            <td>
                              <Badge
                                bg={
                                  spot.status === "available"
                                    ? "success"
                                    : spot.status === "occupied"
                                      ? "danger"
                                      : spot.status === "reserved"
                                        ? "warning"
                                        : "secondary"
                                }
                                className="px-3 py-2"
                              >
                                {spot.status.charAt(0).toUpperCase() +
                                  spot.status.slice(1)}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="info" className="px-3 py-2">
                                {spot.size}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeParkingSpot(index)}
                                className="px-3 py-1"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            {/* Section 4: Yard Settings */}
            <div className="mb-4">
              <div className="d-flex align-items-center mb-4">
                <div className="bg-success bg-opacity-10 p-2 rounded-circle me-3">
                  <FaLayerGroup className="text-success" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Yard Settings</h5>
                  <small className="text-muted">
                    Configure yard status and permissions
                  </small>
                </div>
              </div>

              <Row>
                <Col md={6} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <Form.Check
                          type="switch"
                          id="isVerified"
                          name="isVerified"
                          label={
                            <div>
                              <div className="fw-bold">Verified Yard</div>
                              <small className="text-muted">
                                Mark this yard as verified in the system
                              </small>
                            </div>
                          }
                          checked={formData.isVerified}
                          onChange={handleChange}
                          className="form-check-lg"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <Form.Check
                          type="switch"
                          id="isActive"
                          name="isActive"
                          label={
                            <div>
                              <div className="fw-bold">Active Status</div>
                              <small className="text-muted">
                                Set yard as active or inactive
                              </small>
                            </div>
                          }
                          checked={formData.isActive}
                          onChange={handleChange}
                          className="form-check-lg"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Form Actions */}
            <div className="border-top pt-4 mt-4">
              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate("/yards/list")}
                  className="px-4 py-2 rounded-pill"
                >
                  <FaTimes className="me-2" />
                  Cancel
                </Button>

                <div className="d-flex flex-column align-items-end">
                  <Button
                    variant={isFormReady ? "primary" : "secondary"}
                    type="submit"
                    disabled={loading || !isFormReady}
                    className="px-5 py-2 rounded-pill shadow"
                    style={{
                      background: isFormReady
                        ? "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)"
                        : "",
                      border: "none",
                      minWidth: "180px",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Update Yard
                      </>
                    )}
                  </Button>
                  {!isFormReady && (
                    <small className="text-muted mt-2 text-end">
                      <FaInfoCircle className="me-1" />
                      Fill all required fields to enable update
                    </small>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </Card.Body>

        {/* Card Footer */}
        <Card.Footer className="bg-light py-3">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              <FaInfoCircle className="me-1" />
              Fields marked with * are required
            </small>
            <small className="text-muted">Yard ID: {id}</small>
          </div>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default EditYard;
